import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { bulkDeleteReports, deleteReport } from '../services/reportApi';
import { formatCount, formatDateTime, formatRequestId } from '../utils/format';

function ratioEntries(ratios) {
  return Object.entries(ratios || {}).filter((entry) => Number(entry[1]) > 0);
}

function labelize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function ReportsPanel({ downloadingId, loading, onDownload, reports, page, search, total, onPageChange, onSearchChange, onRefresh, onDeleteSuccess, onViewDetail }) {
  const [deletingId, setDeletingId] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  async function handleDelete(report) {
    if (!window.confirm(`Are you sure you want to delete ${report.filename || 'this report'}?`)) {
      return;
    }
    setDeletingId(report.id);
    try {
      await deleteReport(report.id);
      toast.success(`Report ${report.filename || report.id} deleted successfully.`);
      if (onDeleteSuccess) onDeleteSuccess(report.id);
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to delete report.');
    } finally {
      setDeletingId('');
    }
  }

  async function handleBulkDelete() {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected reports?`)) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      const response = await bulkDeleteReports(selectedIds);
      toast.success(response.message || `Successfully deleted reports.`);
      setSelectedIds([]);
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to delete reports.');
    } finally {
      setIsBulkDeleting(false);
    }
  }

  function handleSelectAll(e) {
    if (e.target.checked) {
      setSelectedIds(reports.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  }

  function handleSelect(id, checked) {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  }

  return (
    <aside className="panel reports-panel">
      <div className="panel__header">
        <div className="panel__header--split">
          <div>
            <span className="panel__eyebrow">Report library</span>
            <h2>Saved reports</h2>
            <p>Every generated workbook stays available here for direct Excel download.</p>
          </div>
          <div>
            <input 
              type="text" 
              placeholder="Search reports..." 
              value={search} 
              onChange={(e) => onSearchChange(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: '12px', border: '1px solid rgba(84, 104, 131, 0.22)', outline: 'none', background: 'rgba(255, 255, 255, 0.94)' }}
            />
          </div>
        </div>
      </div>

      {loading ? <div className="empty-state">Loading saved reports...</div> : null}

      {!loading && reports.length === 0 ? (
        <div className="empty-state">
          {search ? "Not available in the database" : "No reports yet. Generate your first workbook from the form."}
        </div>
      ) : null}

      {!loading && reports.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input 
              type="checkbox" 
              checked={reports.length > 0 && selectedIds.length === reports.length}
              onChange={handleSelectAll}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>Select All ({selectedIds.length} selected)</span>
          </div>
          {selectedIds.length > 0 && (
            <button 
              className="button button--secondary" 
              onClick={handleBulkDelete} 
              disabled={isBulkDeleting}
              style={{ color: '#ef4444', borderColor: '#fee2e2', background: '#fef2f2', minHeight: '36px' }}
            >
              {isBulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}
            </button>
          )}
        </div>
      )}

      {!loading && reports.length > 0 ? (
        <div className="report-list">
          {reports.map((report, index) => {
            const ratios = ratioEntries(report.ratios);
            const isDownloading = downloadingId === report.id;
            const isDeleting = deletingId === report.id;
            const isSelected = selectedIds.includes(report.id);
            const serialNumber = (page - 1) * 10 + index + 1;

            return (
              <article className={`report-card ${isSelected ? 'report-card--selected' : ''}`} key={report.id} style={isSelected ? { borderColor: '#10b981', boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.2)' } : {}}>
                <div className="report-card__top">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={(e) => handleSelect(report.id, e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', fontSize: '0.85rem', color: '#64748b' }}>#{serialNumber}</span>
                        {report.filename || 'Generated report'}
                      </h3>
                      <p>{formatRequestId(report.id)}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="button button--ghost" onClick={() => onViewDetail(report)} type="button" style={{ padding: '0 12px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      View
                    </button>
                    <button className="button button--secondary" disabled={isDeleting} onClick={() => handleDelete(report)} type="button" style={{ padding: '0 12px' }}>
                      {isDeleting ? '...' : 'Delete'}
                    </button>
                    <button className="button button--ghost" disabled={isDownloading} onClick={() => onDownload(report)} type="button">
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </button>
                  </div>
                </div>

                <div className="report-card__meta">
                  <span>{formatCount(report.count)} rows</span>
                  <span>{report.rate || 0} msg/sec</span>
                  <span>{formatDateTime(report.createdAt)}</span>
                </div>

                <div className="report-card__ratios">
                  {ratios.length > 0 ? ratios.map(([key, value]) => (
                    <span className="ratio-tag" key={key}>
                      {labelize(key)} {value}
                    </span>
                  )) : <span className="muted-text">Default sent ratio</span>}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {!loading && total > 10 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <span className="muted-text" style={{ fontSize: '0.9rem' }}>Showing page {page} of {Math.ceil(total / 10)}</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="button button--secondary" disabled={page === 1} onClick={() => onPageChange(page - 1)} style={{ minHeight: '36px', padding: '0 14px' }}>Previous</button>
            <button className="button button--secondary" disabled={page >= Math.ceil(total / 10)} onClick={() => onPageChange(page + 1)} style={{ minHeight: '36px', padding: '0 14px' }}>Next</button>
          </div>
        </div>
      )}
    </aside>
  );
}
